import React, { useEffect, useState } from "react";
import { Table, Spin, Button, Tooltip, Tabs, Card, Typography, Badge } from "antd";
import { formatDate } from "../utils/dateFormatter";
import useUserData from "../hooks/useUserData";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";

// === All Tab Definitions ===
const TabData = [
  { key: "engine-log", label: "Engine Log Book" },
  { key: "deck-log", label: "Deck Log Book" },
  { key: "oil-record-part-i", label: "Oil Record Book Part I" },
  { key: "oil-record-part-ii", label: "Oil Record Book Part II" },
  { key: "cargo-record", label: "Cargo Record Book" },
  { key: "garbage-record-part-i", label: "Garbage Record Part I" },
  { key: "garbage-record-part-ii", label: "Garbage Record Part II" },
  { key: "ballast-record", label: "Ballast Record Book" },
  { key: "sulphur-record-part-i", label: "Sulphur Record Part I" },
  { key: "sulphur-record-part-ii", label: "Sulphur Record Part II" },
  { key: "sulphur-record-part-iii", label: "Sulphur Record Part III" },
];

// === Example Designation-based Access Rules ===
const accessMap = {
  "Chief Engineer": [
    "engine-log",
    "oil-record-part-i",
    "sulphur-record-part-i",
    "sulphur-record-part-ii",
    "sulphur-record-part-iii",
  ],
  Captain: [
    "deck-log",
    "cargo-record",
    "oil-record-part-i",
    "oil-record-part-ii",
    "ballast-record",
    "garbage-record-part-i",
    "garbage-record-part-ii",
  ],
  "Chief Officer": ["oil-record-part-ii"],
};

const PendingTaskTab = ({ getActiveTab }) => {
  const navigate = useNavigate();
  const user = useUserData();
  const designation = user?.data?.designation || "";
  const [activeTab, setActiveTab] = useState("");

  const [tabDataSource, setTabDataSource] = useState([]);
  const [loading, setLoading] = useState(false);

  // Filter tabs based on designation
  const filteredTabs = React.useMemo(() => {
    const allowedKeys = accessMap[designation] || [];
    return TabData.filter((tab) => allowedKeys.includes(tab.key));
  }, [designation]);

  useEffect(() => {
    const lastKey =
      designation === "Chief Engineer"
        ? "engine-log"
        : designation === "Captain"
        ? "deck-log"
        : designation === "Chief Officer"
        ? "oil-record-part-ii"
        : "";
    setActiveTab(lastKey);
  }, [designation]);

  useEffect(() => {
    const fetchData = async () => {
      if (!getActiveTab) return;
      setLoading(true);
      try {
        const result = await getActiveTab(activeTab);
        setTabDataSource(Array.isArray(result?.data) ? result.data : []);
      } catch (err) {
        console.error("Error fetching tab data:", err);
        setTabDataSource([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [activeTab]);

  const handleClick = (key) => {
    setActiveTab(key);
  };

  const renderStatus = (record) => {
    // record is the whole row object, not just the value
    if (record.pending_by_chief_engineer === false) {
      return (
        <span className="table-status-deactivate">Awaiting C/E Review</span>
      );
    }
    if (record.pending_by_master === false) {
      return (
        <span className="table-status-deactivate">Awaiting Master Review</span>
      );
    }
    if (record.pending_by_chief_officier === false) {
      return (
        <span className="table-status-deactivate">Awaiting C/O Review</span>
      );
    }

    // If all reviews are done
    return <span className="table-status-activate">Reviewed</span>;
  };

  // ✅ Full column definitions per tab
  const getColumnsForTab = (tabKey) => {
    switch (tabKey) {
      case "engine-log":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "date_time_log", align: "center" },

          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-engine-basic-info/${record?.id}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "deck-log":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "log_date_time", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-deck-log/${btoa(record.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];
      case "cargo-record":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "date", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-cargo-record/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "oil-record-part-i":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "date", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(
                            `/edit-oil-record-log-one/${btoa(record?.id)}`
                          )
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "oil-record-part-ii":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "date", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-oil-record-log/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "garbage-record-part-i":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "log_date_time", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-garbage-log/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "garbage-record-part-ii":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "log_date_time", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-garbage-log-two/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "sulphur-record-part-i":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "bunker_date", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-bunker-details/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "sulphur-record-part-ii":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "bunkerDate", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(
                            `/edit-bunker-delivery-note/${btoa(record?.id)}`
                          )
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "sulphur-record-part-iii":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          {
            title: "Entry Date",
            dataIndex: "date_and_time_completion",
            align: "center",
          },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-fuel-changeover/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      case "ballast-record":
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          {
            title: "Entry Date",
            dataIndex: "date",
            align: "center",
          },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Updated Date",
            dataIndex: "updated_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
          {
            title: "Action",
            align: "center",
            key: "Action",
            render: (text, record) => {
              return (
                <>
                  <Tooltip title={"Edit"}>
                    <span>
                      <Button
                        type="primary"
                        size="small"
                        onClick={() =>
                          navigate(`/edit-ballast-record/${btoa(record?.id)}`)
                        }
                        style={{ marginRight: "5px" }}
                        icon={<EditOutlined />}
                      />
                    </span>
                  </Tooltip>
                </>
              );
            },
          },
        ];

      default:
        return [
          {
            title: " ID",
            dataIndex: "id",
            align: "center",
          },
          { title: "Entry Date", dataIndex: "date", align: "center" },
          { title: "Code Letter", dataIndex: "code", align: "center" },
          {
            title: "Created Date",
            dataIndex: "created_at",
            align: "center",
            render: formatDate,
          },
          {
            title: "Current Status",
            dataIndex: "current_status",
            align: "center",
            render: (_, record) => renderStatus(record),
          },
        ];
    }
  };

  const columns = getColumnsForTab(activeTab);

  return (
    <Card 
      className="hover-lift-card"
      style={{ borderRadius: "20px", boxShadow: "0 10px 40px -10px rgba(0,0,0,0.08)", border: "1px solid rgba(255,255,255,0.4)", backdropFilter: "blur(20px)", background: "rgba(255,255,255,0.85)" }}
      bodyStyle={{ padding: "28px" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <Typography.Title level={4} style={{ margin: 0, fontWeight: 800, color: "#0f172a", letterSpacing: "-0.5px" }}>
          📋 Pending Tasks Management
        </Typography.Title>
      </div>

      <div className="premium-tabs">
        <Tabs 
          activeKey={activeTab} 
          onChange={handleClick} 
          items={filteredTabs.map(tab => ({ key: tab.key, label: tab.label }))}
          type="card"
          tabBarStyle={{ marginBottom: "24px" }}
        />
      </div>

      <Spin spinning={loading}>
        <Table
          bordered={false}
          columns={columns}
          dataSource={Array.isArray(tabDataSource) ? tabDataSource : []}
          pagination={{ pageSize: 10, showSizeChanger: true }}
          rowKey={(record) =>
            record.eng_log_basic_id ||
            record.deck_id ||
            record.oil_record_part_1 ||
            record.oil_record_part_2 ||
            record.cargo_id ||
            record.garbage_part_1_id ||
            record.garbage_part_2_id ||
            record.ballast_id ||
            record.sulphur_id ||
            record.id
          }
          className="custom-modern-table"
        />
      </Spin>
    </Card>
  );
};

export default PendingTaskTab;
